import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../Context/AuthContext";
import { useAlertContext } from "../Context/AlertContext";
import { useTranslation } from "react-i18next";
import AuthAlerts from "./Auth/components/AuthAlerts";
import AuthShell from "./Auth/components/AuthShell";
import ForgotPasswordView from "./Auth/components/ForgotPasswordView";
import LoginView from "./Auth/components/LoginView";
import RegisterView from "./Auth/components/RegisterView";
import "./Auth/auth-theme.css";
import "./Auth/forgot-auth.css";

const AUTH_VIEWS = ["login", "register", "forgot"];

const Login = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeView, setActiveView] = useState("login");
  const [forgotStep, setForgotStep] = useState("request");
  const [inlineError, setInlineError] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const { submitting, login, register, forgotPassword, resetPassword } = useAuthContext();
  const { setSuccess, errors, clearErrors } = useAlertContext();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const siteName = process.env.REACT_APP_WESITE_NAME || "Odoo API";

  useEffect(() => {
    const view = searchParams.get("view");
    if (view && AUTH_VIEWS.includes(view)) {
      setActiveView(view);
      if (view !== "forgot") {
        setForgotStep("request");
      }
    } else if (!view) {
      setActiveView("login");
    }
  }, [searchParams]);

  useEffect(() => {
    if (errors?.message) {
      setInlineError(errors.message);
    }
  }, [errors]);

  useEffect(() => {
    document.body.classList.remove("landing-active", "auth-active", "forgot-auth-active");

    if (activeView === "forgot") {
      document.body.classList.add("forgot-auth-active");
    } else {
      document.body.classList.add("auth-active");
    }

    return () => {
      document.body.classList.remove("auth-active", "forgot-auth-active");
    };
  }, [activeView]);

  const switchView = useCallback(
    (view) => {
      setInlineError("");
      clearErrors();
      setActiveView(view);
      if (view === "login") {
        setSearchParams({});
        setForgotStep("request");
        return;
      }
      setSearchParams({ view });
      if (view !== "forgot") {
        setForgotStep("request");
      }
    },
    [setSearchParams, clearErrors]
  );

  const handleLoginSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setInlineError("");
      clearErrors();
      try {
        await login({
          username: loginEmail,
          password: loginPassword
        });
      } catch (e) {
        const message = e.message || t("Invalid email or password. Please try again.");
        setInlineError(message);
        if (e.code === "UserNotConfirmedException") {
          navigate("/verify", { state: { email: loginEmail } });
        }
      }
    },
    [loginEmail, loginPassword, login, navigate, clearErrors, t]
  );

  const handleRegisterSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setInlineError("");
      clearErrors();

      if (registerPassword !== confirmPassword) {
        setInlineError(t("Passwords do not match. Please check and try again."));
        return;
      }

      try {
        await register({
          username: registerEmail,
          password: registerPassword,
          attributes: { email: registerEmail }
        });
        setSuccess(t("We have sent you an email for verify your account!"), true);
        navigate("/verify", { state: { email: registerEmail } });
      } catch (e) {
        setInlineError(e.message);
      }
    },
    [
      registerEmail,
      registerPassword,
      confirmPassword,
      register,
      setSuccess,
      t,
      navigate,
      clearErrors
    ]
  );

  const handleForgotSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setInlineError("");
      clearErrors();
      try {
        await forgotPassword(forgotEmail);
        setForgotStep("reset");
        setSuccess(
          t("We have sent you an email with the verification code to reset your password"),
          true
        );
      } catch (e) {
        setInlineError(e.message);
      }
    },
    [forgotEmail, forgotPassword, setSuccess, t, clearErrors]
  );

  const handleResetSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setInlineError("");
      clearErrors();
      try {
        await resetPassword(forgotEmail, resetCode, newPassword);
        setForgotStep("request");
        setResetCode("");
        setNewPassword("");
        switchView("login");
        setSuccess(t("Password reset successful. Try logging in!"), true);
      } catch (e) {
        setInlineError(e.message);
      }
    },
    [forgotEmail, resetCode, newPassword, resetPassword, setSuccess, t, switchView, clearErrors]
  );

  const resendMail = async (event) => {
    event.preventDefault();
    setInlineError("");
    clearErrors();
    try {
      await forgotPassword(forgotEmail);
      setSuccess(
        t("We have sent you an email with the verification code to reset your password"),
        true
      );
    } catch (e) {
      setInlineError(e.message);
    }
  };

  if (activeView === "forgot") {
    return (
      <>
        <AuthAlerts />
        <ForgotPasswordView
          forgotStep={forgotStep}
          forgotEmail={forgotEmail}
          resetCode={resetCode}
          newPassword={newPassword}
          submitting={submitting}
          inlineError={inlineError}
          onForgotEmailChange={setForgotEmail}
          onResetCodeChange={setResetCode}
          onNewPasswordChange={setNewPassword}
          onForgotSubmit={handleForgotSubmit}
          onResetSubmit={handleResetSubmit}
          onResendMail={resendMail}
          onBackToRequest={() => setForgotStep("request")}
          onLoginClick={() => switchView("login")}
        />
      </>
    );
  }

  return (
    <>
      <AuthAlerts />
      <AuthShell wide={activeView === "register"} heroVariant={activeView}>
        {activeView === "login" ? (
          <LoginView
            siteName={siteName}
            loginEmail={loginEmail}
            loginPassword={loginPassword}
            submitting={submitting}
            inlineError={inlineError}
            onEmailChange={(value) => {
              setLoginEmail(value);
              setInlineError("");
              clearErrors();
            }}
            onPasswordChange={(value) => {
              setLoginPassword(value);
              setInlineError("");
              clearErrors();
            }}
            onSubmit={handleLoginSubmit}
            onForgotClick={() => switchView("forgot")}
            onRegisterClick={() => switchView("register")}
          />
        ) : (
          <RegisterView
            siteName={siteName}
            registerEmail={registerEmail}
            registerPassword={registerPassword}
            confirmPassword={confirmPassword}
            termsAccepted={termsAccepted}
            submitting={submitting}
            inlineError={inlineError}
            onEmailChange={(value) => {
              setRegisterEmail(value);
              setInlineError("");
              clearErrors();
            }}
            onPasswordChange={(value) => {
              setRegisterPassword(value);
              setInlineError("");
              clearErrors();
            }}
            onConfirmPasswordChange={(value) => {
              setConfirmPassword(value);
              setInlineError("");
              clearErrors();
            }}
            onTermsChange={setTermsAccepted}
            onSubmit={handleRegisterSubmit}
            onLoginClick={() => switchView("login")}
          />
        )}
      </AuthShell>
    </>
  );
};

export default Login;
